import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";
import {
  encodeRowAnswer,
  restoreRowLabels,
  rowAnswer,
  rowLabelDraft,
  updateRowLabel,
  validateRowLabels,
  type RowLabelDraft,
} from "../specify/core";
import type { GridJsonResponse, SpecifiedRowAnswer } from "../specify/types";

export type GridNumericPrefill = Readonly<
  Record<string, number | Readonly<Record<string, number>>>
>;

type GridNumericCellPrefill = Readonly<
  Record<string, Readonly<Record<string, number>>>
>;

export type GridNumericRaw = RawVisualGrouping & {
  type: "grid_numeric";
  content: string;
  columns?: string[];
  rows: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
    pop_up_text?: string | null;
  }[];
  dont_know_option?: { value: string; text: string } | null;
  autosum_rows?: boolean;
  autosum_columns?: boolean;
  previous_response?: (
    | string
    | SpecifiedRowAnswer<string>
    | (string | SpecifiedRowAnswer<string>)[]
  )[];
  prefill?: GridNumericPrefill;
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type GridNumericDefinition = VisualGrouping & {
  prompt: string;
  isList: boolean;
  columns: readonly string[];
  rows: readonly {
    id: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    customPrompt?: string;
  }[];
  dontKnow?: { value: string; label: string };
  autosumRows: boolean;
  autosumColumns: boolean;
  previousRowLabels?: Readonly<Record<string, string>>;
  previousResponses: readonly (readonly string[])[];
  prefill: GridNumericCellPrefill;
  image?: string;
  altText?: string;
  error?: string;
};

export type GridNumericDraft = RowLabelDraft & {
  valuesByRow: Readonly<Record<string, readonly string[]>>;
};
export type GridNumericEvent =
  | {
      type: "set-cell";
      rowId: string;
      columnIndex: number;
      value: string;
    }
  | { type: "toggle-dont-know"; rowId: string }
  | { type: "set-row-label"; rowId: string; value: string };
export type GridNumericResponse = {
  type: "grid_numeric";
  value: string;
  responses: string;
  responseJson?: GridJsonResponse;
};

const integer = (value: string) => /^-?\d+$/.test(value.trim());

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const gridNumericTotals = (
  definition: GridNumericDefinition,
  draft: GridNumericDraft,
): readonly { value: string; complete: boolean }[] => {
  const groups = definition.autosumColumns
    ? definition.columns.map((_, columnIndex) =>
        definition.rows.map(
          (row) => draft.valuesByRow[row.id]?.[columnIndex] ?? "",
        ),
      )
    : definition.rows.map((row) => draft.valuesByRow[row.id] ?? []);
  return groups.map((values) => ({
    value:
      definition.dontKnow && values.includes(definition.dontKnow.value)
        ? ""
        : values
            .reduce<bigint>(
              (sum, value) =>
                sum + (integer(value) ? BigInt(value.trim()) : 0n),
              0n,
            )
            .toString(),
    complete:
      values.length ===
        (definition.autosumColumns
          ? definition.rows.length
          : definition.columns.length) && values.every(integer),
  }));
};

export const gridNumericCore: QuestionCore<
  GridNumericRaw,
  GridNumericDefinition,
  GridNumericDraft,
  GridNumericEvent,
  GridNumericResponse
> = {
  decode: (raw) => {
    const isList = raw.columns === undefined;
    if (!Array.isArray(raw.rows) || raw.rows.length === 0) {
      throw new Error("Grid numeric requires at least one row.");
    }
    if (
      raw.columns !== undefined &&
      (!Array.isArray(raw.columns) ||
        raw.columns.length === 0 ||
        raw.columns.some((column) => typeof column !== "string"))
    ) {
      throw new Error(
        "Grid numeric columns must be a non-empty list of labels.",
      );
    }
    const rowIds = raw.rows.map((row) => row.value);
    if (
      rowIds.some((id) => typeof id !== "string" || !id) ||
      new Set(rowIds).size !== rowIds.length
    ) {
      throw new Error("Grid numeric row IDs must be unique.");
    }
    if (raw.autosum_rows && raw.autosum_columns) {
      throw new Error("Grid numeric cannot autosum both rows and columns.");
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => {
          if (isList)
            return (
              typeof rowAnswer(value as string | SpecifiedRowAnswer<string>) !==
              "string"
            );
          return (
            typeof value !== "string" &&
            (!Array.isArray(value) ||
              value.some((cell) => typeof rowAnswer(cell) !== "string"))
          );
        }))
    ) {
      throw new Error(
        "Grid numeric previous response must be a list of strings, or column arrays for a grid.",
      );
    }
    if (
      raw.prefill !== undefined &&
      (!isRecord(raw.prefill) ||
        Object.values(raw.prefill).some((row) =>
          isList
            ? typeof row !== "number" || !Number.isFinite(row)
            : !isRecord(row) ||
              Object.values(row).some(
                (value) => typeof value !== "number" || !Number.isFinite(value),
              ),
        ))
    ) {
      throw new Error(
        "Grid numeric prefill must map row IDs to finite numeric values for a list or column objects with finite numeric values for a grid.",
      );
    }
    const saved = raw.previous_response ?? [];
    const columns =
      isList && saved.length > 0
        ? [saved as (string | SpecifiedRowAnswer<string>)[]]
        : saved.map((column) =>
            typeof column === "string"
              ? column.split(",")
              : (column as (string | SpecifiedRowAnswer<string>)[]),
          );
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      isList,
      columns: raw.columns ?? [""],
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        displayLabel: row.display_text,
        ...(row.pop_up_text != null ? { customPrompt: row.pop_up_text } : {}),
        ...(row.image ? { image: row.image } : {}),
      })),
      autosumRows: Boolean(raw.autosum_rows),
      autosumColumns: Boolean(raw.autosum_columns),
      previousResponses: columns.map((column) =>
        column.map((value) => rowAnswer(value)),
      ),
      ...(raw.rows.some((row) => row.pop_up_text != null)
        ? {
            previousRowLabels: restoreRowLabels(
              raw.rows.map((row) => ({
                id: row.value,
                customPrompt: row.pop_up_text ?? undefined,
              })),
              columns,
            ),
          }
        : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      prefill: Object.fromEntries(
        Object.entries(raw.prefill ?? {}).map(([rowId, value]) => [
          rowId,
          typeof value === "number" ? { "": value } : value,
        ]),
      ),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const valuesByRow = Object.create(null) as Record<
      string,
      readonly string[]
    >;

    const hasPreviousResponse = definition.previousResponses.length > 0;

    definition.rows.forEach((row, rowIndex) => {
      valuesByRow[row.id] = definition.columns.map((column, columnIndex) => {
        // Restore saved answers, including intentionally empty cells.
        if (hasPreviousResponse) {
          return definition.previousResponses[columnIndex]?.[rowIndex] ?? "";
        }

        const value = definition.prefill[row.id]?.[column];

        return value == null ? "" : String(value);
      });
    });

    return { valuesByRow, ...rowLabelDraft(definition.previousRowLabels) };
  },
  update: (definition, draft, event) => {
    if (event.type === "set-row-label") {
      return definition.rows.some(
        (row) => row.id === event.rowId && row.customPrompt !== undefined,
      )
        ? {
            ...draft,
            specifiedRows: updateRowLabel(
              draft.specifiedRows,
              event.rowId,
              event.value,
            ),
          }
        : draft;
    }
    if (event.type === "toggle-dont-know") {
      if (!definition.rows.some((row) => row.id === event.rowId)) return draft;
      const values = draft.valuesByRow[event.rowId];
      const selected =
        definition.dontKnow !== undefined &&
        values.length === definition.columns.length &&
        values.every((value) => value === definition.dontKnow?.value);
      if (!definition.dontKnow) return draft;
      return {
        ...draft,
        valuesByRow: {
          ...draft.valuesByRow,
          [event.rowId]: definition.columns.map(() =>
            selected ? "" : definition.dontKnow!.value,
          ),
        },
      };
    }
    if (
      !definition.rows.some((row) => row.id === event.rowId) ||
      !Number.isInteger(event.columnIndex) ||
      event.columnIndex < 0 ||
      event.columnIndex >= definition.columns.length
    )
      return draft;
    const values = [...draft.valuesByRow[event.rowId]].map((value) =>
      definition.dontKnow && value === definition.dontKnow.value ? "" : value,
    );
    values[event.columnIndex] = event.value;
    return {
      ...draft,
      valuesByRow: { ...draft.valuesByRow, [event.rowId]: values },
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    const labels = validateRowLabels(
      definition.rows,
      draft.specifiedRows ?? {},
    );
    if (!labels.valid) return labels;
    return definition.rows.every((row) => {
      const values = draft.valuesByRow[row.id] ?? [];
      return (
        values.length === definition.columns.length &&
        (values.every(integer) ||
          (definition.dontKnow !== undefined &&
            values.every((value) => value === definition.dontKnow?.value)))
      );
    })
      ? { valid: true }
      : { valid: false, error: copy.validation.wholeNumbers };
  },
  encode: (definition, draft) => {
    const validation = gridNumericCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values = definition.columns.flatMap((_, columnIndex) =>
      definition.rows.map((row) =>
        draft.valuesByRow[row.id][columnIndex].trim(),
      ),
    );
    return {
      type: "grid_numeric",
      value: values.join(","),
      responses: encodeResponses(
        values,
        definition.isList ? undefined : definition.rows.length,
      ),
      ...(definition.rows.some((row) => row.customPrompt !== undefined)
        ? {
            responseJson: definition.isList
              ? definition.rows.map((row) =>
                  encodeRowAnswer(
                    row,
                    draft.specifiedRows!,
                    draft.valuesByRow[row.id][0].trim(),
                  ),
                )
              : definition.columns.map((_, columnIndex) =>
                  definition.rows.map((row) =>
                    encodeRowAnswer(
                      row,
                      draft.specifiedRows!,
                      draft.valuesByRow[row.id][columnIndex].trim(),
                    ),
                  ),
                ),
          }
        : {}),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridNumericCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_numeric",
      question: definition.prompt,
      answer: definition.rows
        .map(
          (row) =>
            `${draft.specifiedRows?.[row.id] || row.label}: ${
              definition.dontKnow &&
              draft.valuesByRow[row.id].every(
                (value) => value === definition.dontKnow!.value,
              )
                ? definition.dontKnow!.label
                : definition.columns
                    .map(
                      (column, index) =>
                        `${column ? `${column} ` : ""}${draft.valuesByRow[row.id][index]}`,
                    )
                    .join(", ")
            }`,
        )
        .join("\n"),
    };
  },
};
